import React from 'react';
import { StyleSheet, Text, View, Modal, ScrollView } from 'react-native';
import { mdiArrowLeft } from '@mdi/js';
import GenericButton from '../molecules/GenericButton';
import OfferSuccess from '../../features/offer/OfferSuccess';
import Header from '../layout/Header';
import HeaderButton from '../layout/HeaderButton';
import CredentialCard from '../../features/credential/card/CredentialCard';
import OfferDetails from '../../features/offer/OfferDetails';
import { IThemeContext, useThemeContext } from '../../contexts';
import { CredentialExchangeRecord } from '../../types/credentials';

interface CredentialOfferModalProps {
  visible: boolean;
  onClose: () => void;
  accepted: boolean;
  credential: CredentialExchangeRecord;
  primaryAction: { title: string; action: () => void };
  secondaryAction: { title: string; action: () => void };
  onAccept: () => void;
  onReject: () => void;
}

/**
 * @description Modal component for displaying credential offer details
 * @param {CredentialOfferModalProps} props - Component properties
 * @return {React.FC}
 */
const CredentialOfferModal: React.FC<CredentialOfferModalProps> = ({
  visible,
  onClose,
  accepted,
  credential,
  primaryAction,
  secondaryAction,
  onAccept,
  onReject
}) => {
  const { theme } = useThemeContext();
  const styles = createCredentialOfferModalStyles(theme);

  return (
    <Modal visible={visible} onRequestClose={onClose} animationType="fade">
      <Header
        title={accepted ? 'Credential Offer Accepted' : 'Credential Offer Details'}
        leftButton={<HeaderButton icon={mdiArrowLeft} onPress={onClose} />}
      />

      {accepted ? (
        <OfferSuccess credential={credential} />
      ) : (
        <View style={styles.modalContent}>
          <ScrollView contentContainerStyle={styles.scrollContainer}>
            <View style={styles.contentWrapper}>
              {/* Header Section */}
              <View style={styles.modalHeader}>
                <Text style={styles.reviewOfferHeader}>Credential Offer</Text>
                <Text style={styles.reviewOfferText}>
                  Review the information that will be associated with this credential.
                </Text>
              </View>

              {/* Offer Details Section */}
              <View>
                <Text style={styles.offerDetailHeader}>Offer Details</Text>
              </View>

              {/* Credential Card */}
              <View style={styles.cardContainer}>
                <CredentialCard credential={credential} />
              </View>

              {/* Additional Details */}
              <OfferDetails credential={credential} />

              {/* Action Buttons */}
              <View style={styles.buttonContainer}>
                <GenericButton
                  title={primaryAction.title}
                  onPress={onAccept}
                  buttonStyle={styles.primaryButton}
                  textStyle={styles.primaryButtonText}
                  isPrimary={true}
                />
                <GenericButton
                  title={secondaryAction.title}
                  onPress={onReject}
                  buttonStyle={styles.secondaryButton}
                  textStyle={styles.secondaryButtonText}
                  isPrimary={false}
                />
              </View>
            </View>
          </ScrollView>
        </View>
      )}
    </Modal>
  );
};

const createCredentialOfferModalStyles = (
  theme: IThemeContext['theme']
): ReturnType<typeof StyleSheet.create> =>
  StyleSheet.create({
    // Modal styles
    modalContent: {
      flex: 1
    },
    scrollContainer: {
      padding: 24
    },
    contentWrapper: {
      maxWidth: 500,
      alignSelf: 'center',
      width: '100%',
      gap: 24
    },

    // Header styles
    modalHeader: {
      gap: 8
    },
    reviewOfferHeader: {
      fontWeight: '700',
      fontSize: 24,
      color: theme.textPrimary
    },
    reviewOfferText: {
      fontWeight: '400',
      fontSize: 16,
      color: theme.textSecondary,
      lineHeight: 24
    },
    offerDetailHeader: {
      fontWeight: '700',
      fontSize: 16,
      color: theme.textPrimary
    },

    // Button container styles
    buttonContainer: {
      flexDirection: 'row',
      justifyContent: 'space-between',
      gap: 16,
      paddingTop: 24,
      paddingBottom: 20
    },
    primaryButton: {
      backgroundColor: theme.primary,
      flex: 1,
      height: 53,
      borderRadius: 6
    },
    primaryButtonText: {
      fontSize: 18,
      fontWeight: '700',
      color: 'white'
    },
    secondaryButton: {
      backgroundColor: 'white',
      flex: 1,
      height: 53,
      borderRadius: 6,
      borderColor: 'black',
      borderWidth: 1
    },
    secondaryButtonText: {
      fontSize: 18,
      fontWeight: '700',
      color: 'black'
    }
  });

export default CredentialOfferModal;
